import useAuth from 'hooks/useAuth';
import { useSelector } from 'store';
import { useStartTrackingTimecardMutation, useUpdateTimecardCacheMutation } from 'store/api/timecards';
import { TimecardDetails } from 'types/timecards';
import { logMessage } from 'utils/monitoring';
import { captureTimerStarted } from 'utils/timecardAnalytics';

type UseStartTrackingTimecard = {
    startTracking: (timecardId: number) => Promise<TimecardDetails | undefined>;
};

export const useStartTrackingTimecard = (): UseStartTrackingTimecard => {
    const { trackedTimecardId, isStopwatchRunning } = useSelector((state) => state.timecard);
    const [startTracking] = useStartTrackingTimecardMutation();
    const [updateTimecardCache] = useUpdateTimecardCacheMutation();
    const { user } = useAuth();

    const startTrackingProcess = async (timecardId: number) => {
        if (!user?.id || !user?.activeTenantId) {
            logMessage('User data not available for timecard tracking');
            throw new Error('User data not available for timecard tracking');
        }

        const previouslyRunningTimecardId =
            isStopwatchRunning && trackedTimecardId && parseInt(trackedTimecardId) !== timecardId
                ? parseInt(trackedTimecardId)
                : undefined;
        // A failed start rejects here, so the caller can show its error.
        const result = await startTracking({
            id: timecardId,
            userId: user.id,
            tenantId: user.activeTenantId,
        }).unwrap();
        captureTimerStarted();
        if (previouslyRunningTimecardId !== undefined) {
            updateTimecardCache(previouslyRunningTimecardId);
        }
        return result;
    };

    return {
        startTracking: startTrackingProcess,
    };
};
